import React, { useEffect, useState } from "react";
import { Box, useMediaQuery, CircularProgress, Typography, IconButton } from "@mui/material";
import { GoogleMap, Marker, InfoWindow, Circle } from "@react-google-maps/api";
import CustomButton from "../../../../components/CustomButton";
import { BASE_URL } from "../../../../components/BaseUrl";
import { useSelector } from "react-redux";
import { decryptToken } from "../../../../Utlis/Encryption";
import { useTranslation } from "react-i18next";
import maparrow from "../../../../assets/maparrow.svg";
import CloseIcon from "@mui/icons-material/Close";
import CustomText, { textStyles } from "../../../../components/CustomText";
import { useMemo } from "react";
import {
  convertLocalDateTimeToUTC,
  convertUTCToLocalDateTime,
} from "../../../../components/dateTimeUtils";
import { getStatusBadgeBackgroundColor } from "../../../../constants/statusPalette";

const mapContainerStyle = { width: "100%", height: "100%" };

const taskStatusPinColor = (status) =>
  getStatusBadgeBackgroundColor(status || "assigned");

/** Map legend: dot colors match StatusDropdown / `statusPalette`. */
const TASK_MAP_LEGEND = [
  { statusKey: "assigned", labelKey: "projectStatus.assigned" },
  { statusKey: "in_progress", labelKey: "projectStatus.in_progress" },
  { statusKey: "completed", labelKey: "projectStatus.completed" },
  // { statusKey: "delayed", labelKey: "projectStatus.delayed" },
  // { statusKey: "overdue", labelKey: "projectStatus.overdue" },
  // { statusKey: "not_done", labelKey: "projectStatus.not_done" },
  { statusKey: "cancelled", labelKey: "projectStatus.cancelled" },
]

const TaskMapLegendItem = ({ color, label, count }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <Box
      sx={{
        width: 14,
        height: 14,
        borderRadius: "50%",
        background: color,
        border: "1px solid #ccc",
      }}
    />
    <Typography sx={{ fontSize: "13px", color: "#333", fontFamily: "Poppins" }}>
      {label}({count ?? 0})
    </Typography>
  </Box>
);

// Create pin markers with status-colored worker names
const createCustomIcon = (workerName, status, taskTitle) => {
  const statusColor = taskStatusPinColor(status);
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="140" height="56" viewBox="0 0 140 56">
      <defs>
        <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="rgba(0,0,0,0.25)"/>
        </filter>
      </defs>
      <g filter="url(#shadow)">
        <g transform="translate(60 2)">
          <rect x="-2" y="-2" width="24" height="24" rx="12" ry="12" fill="transparent"/>
          <path d="M10 0 C15.5 0 20 4.5 20 10 C20 15.5 10 24 10 24 C10 24 0 15.5 0 10 C0 4.5 4.5 0 10 0 Z" fill="${statusColor}"/>
          <circle cx="10" cy="10" r="3.2" fill="#fff"/>
        </g>
        <rect x="10" y="31" width="120" height="22" rx="4" ry="4" fill="rgba(255,255,255,0.95)" stroke="${statusColor}"/>
        <text x="70" y="45" text-anchor="middle" font-family="Arial, sans-serif" font-size="12" font-weight="700" fill="${statusColor}">
          ${String(workerName || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")}
        </text>
      </g>
    </svg>
  `;

  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new window.google.maps.Size(140, 56),
    anchor: new window.google.maps.Point(70, 24),
  };
};

const formatTaskDateTime = (dateTimeValue) => {
  if (!dateTimeValue) return "-";
  const formatted = convertUTCToLocalDateTime(dateTimeValue, {
    locale: "en-GB",
    formatOptions: {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    },
  });
  return formatted === "—" ? "-" : formatted;
};

const getTodayDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const MapTask = ({ onSelectTask, mapTaskId, clearMapTask, statusFilter, priorityFilter,
  clearStatusFilter, clearPriorityFilter, taskViewTab
}) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery("(max-width:768px)");
  const token = useSelector((state) => state.auth.token);
  const decryptedToken = token ? decryptToken(token) : null;
  const DEFAULT_CENTER = [37.0902, -95.7129]; // USA
  const DEFAULT_ZOOM = 4;
  const [markers, setMarkers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isFullMapOpen, setIsFullMapOpen] = useState(false);
  const [activeTask, setActiveTask] = useState(null);
  const [hoveredTask, setHoveredTask] = useState(null);
  const mapRef = React.useRef(null);
  const fullMapRef = React.useRef(null);
  // const visibleMarkers = useMemo(() => {
  //   let result = markers;

  //   if (statusFilter) {
  //     result = result.filter(m => m.status === statusFilter);
  //   }

  //   if (mapTaskId) {
  //     result = result.filter(m => m.id === mapTaskId);
  //   }

  //   return result;
  // }, [markers, statusFilter, mapTaskId]);
  const visibleMarkers = useMemo(() => {
    let result = markers;

    if (statusFilter) {
      result = result.filter(m => m.status === statusFilter);
    }

    if (priorityFilter) {
      result = result.filter(m => m.priority === priorityFilter);
    }

    if (mapTaskId) {
      result = result.filter(m => m.id === mapTaskId);
    }

    return result;
  }, [markers, statusFilter, priorityFilter, mapTaskId]);

  const taskStatusCounts = useMemo(() => {
    return markers.reduce((acc, m) => {
      let s = m.status;
      if (!s) return acc;
      if (s === "canceled") s = "cancelled";
      acc[s] = (acc[s] || 0) + 1;
      return acc;
    }, {});
  }, [markers]);

  const getMapCenter = () =>
    visibleMarkers.length > 0
      ? { lat: visibleMarkers[0].lat, lng: visibleMarkers[0].lng }
      : { lat: DEFAULT_CENTER[0], lng: DEFAULT_CENTER[1] };

  const fitMapToMarkers = (map) => {
    if (!map || !window.google?.maps) return;

    const validMarkers = visibleMarkers.filter(
      (m) => typeof m.lat === "number" && typeof m.lng === "number"
    );

    if (!validMarkers.length) {
      map.setCenter({ lat: DEFAULT_CENTER[0], lng: DEFAULT_CENTER[1] });
      map.setZoom(DEFAULT_ZOOM);
      return;
    }

    if (validMarkers.length === 1) {
      map.setCenter({ lat: validMarkers[0].lat, lng: validMarkers[0].lng });
      map.setZoom(17);
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    validMarkers.forEach((marker) => {
      bounds.extend({ lat: marker.lat, lng: marker.lng });
    });
    map.fitBounds(bounds, 50);
  };

  useEffect(() => {
    if (activeTask) {
      const stillVisible = visibleMarkers.some((marker) => marker.id === activeTask.id);
      if (!stillVisible) setActiveTask(null);
    }
    if (hoveredTask) {
      const stillVisible = visibleMarkers.some((marker) => marker.id === hoveredTask.id);
      if (!stillVisible) setHoveredTask(null);
    }
  }, [visibleMarkers, activeTask, hoveredTask]);

  useEffect(() => {
    fitMapToMarkers(mapRef.current);
    if (isFullMapOpen) {
      fitMapToMarkers(fullMapRef.current);
    }
  }, [visibleMarkers, isFullMapOpen]);

  useEffect(() => {
    setLoading(true);
    let currentPage = 1;
    const allMarkers = [];

    const fetchData = async () => {
      try {
        let hasNextPage = true;
        while (hasNextPage) {
          let url = `${BASE_URL}/task-management/admin/tasks?page=${currentPage}&page_size=50&sort=created_at:desc`;
          if (taskViewTab === "today") {
            const today = getTodayDate();
            const from = convertLocalDateTimeToUTC(`${today}T00:00:00`);
            const to = convertLocalDateTimeToUTC(`${today}T23:59:59`);
            if (from) url += `&from=${encodeURIComponent(from)}`;
            if (to) url += `&to=${encodeURIComponent(to)}`;
          }
          const res = await fetch(url, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${decryptedToken}`,
            },
          });
          const data = await res.json();

          // Check if pagination data exists before accessing hasNext
          if (!data.error && data.data?.tasks) {
            data.data.tasks.forEach((task) => {
              if (task.location_lat && task.location_lng) {
                // Get assigned worker names from assigned_workers array
                const assignedWorkerNames = task.assigned_workers
                  ? task.assigned_workers.map(worker => worker.worker_name).join(', ')
                  : task.assigned_by_name || 'Unknown Worker';

                allMarkers.push({
                  id: task.id,
                  lat: parseFloat(task.location_lat),
                  lng: parseFloat(task.location_lng),
                  title: task.title,
                  description: task.description,
                  status: task.status,
                  priority: task.priority,
                  startAt: task.start_at,
                  endAt: task.end_at,
                  locationAddress: task.location_address,
                  assignedBy: task.assigned_by_name,
                  assignedWorkers: assignedWorkerNames,
                  assignedWorkersCount: task.assigned_workers_count,
                  completedWorkersCount: task.completed_workers_count,
                });
              }
            });

            // Check for pagination before accessing 'hasNext'
            const pagination = data.data.pagination || {};
            hasNextPage = pagination.has_next || false;
            currentPage += 1; // Move to the next page
          } else {
            hasNextPage = false;
          }
        }

        console.log("Total task markers:", allMarkers.length); // Log the number of markers
        setMarkers(allMarkers);
      } catch (err) {
        console.error("Error fetching task map data:", err);
        setMarkers([]);
      } finally {
        setLoading(false);
      }
    };

    const reload = () => fetchData();
    fetchData();
    window.addEventListener("tasks:updated", reload);
    return () => window.removeEventListener("tasks:updated", reload);
  }, [decryptedToken, taskViewTab]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 350 }}>
        <CircularProgress />
      </Box>
    );
  }

  {
    markers.length === 0 && (
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 1000,
          backgroundColor: "white",
          padding: "8px 12px",
          borderRadius: "6px",
          boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
          fontSize: "14px"
        }}
      >
        {t("empty.no_user")}
      </Box>
    )
  }



  // const visibleMarkers = mapTaskId
  //   ? markers.filter(m => m.id === mapTaskId)
  //   : markers;

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      <CustomText
        sx={{
          ...textStyles.h1,
          fontWeight: 600,
          fontSize: "18px",
          color: "#003149",
          textAlign: "left",
          mb: 1.5,
        }}
      >
        {t("livetask")}
      </CustomText>

      <Box
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "center",
          flexWrap: "wrap",
          mb: 1,
          px: 1,
        }}
      >
        {TASK_MAP_LEGEND.map((opt) => (
          <TaskMapLegendItem
            key={opt.statusKey}
            color={taskStatusPinColor(opt.statusKey)}
            label={t(opt.labelKey)}
            count={taskStatusCounts[opt.statusKey]}
          />
        ))}
      </Box>

      <Box sx={{ position: "relative", width: "100%", height: isMobile ? 300 : 400 }}>
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={getMapCenter()}
        zoom={DEFAULT_ZOOM}
        onLoad={(map) => {
          mapRef.current = map;
          fitMapToMarkers(map);
        }}
        onUnmount={() => {
          mapRef.current = null;
        }}
        options={{
          fullscreenControl: false,
          mapTypeControl: false,
          streetViewControl: false,
        }}
      >
        {visibleMarkers.map((task) => (
          <React.Fragment key={task.id}>
            <Circle
              center={{ lat: task.lat, lng: task.lng }}
              radius={task.location_radius_m || 200}
              options={{
                fillColor: taskStatusPinColor(task.status),
                strokeColor: taskStatusPinColor(task.status),
                strokeWeight: 2,
                strokeOpacity: 0.3,
                fillOpacity: 0.1,
              }}
            />

            <Marker
              position={{ lat: task.lat, lng: task.lng }}
              icon={createCustomIcon(task.assignedWorkers, task.status)}
              onClick={() => {
                setActiveTask(task);
                onSelectTask && onSelectTask(task.id);
              }}
              onMouseOver={() => setHoveredTask(task)}
              onMouseOut={() => {
                setHoveredTask((current) => (current?.id === task.id ? null : current));
              }}
            />
          </React.Fragment>
        ))}

        {hoveredTask && (
          <InfoWindow
            position={{ lat: hoveredTask.lat, lng: hoveredTask.lng }}
            onCloseClick={() => setHoveredTask(null)}
          >
            <Box>
              <span style={{ fontWeight: "bold", fontSize: "12px" }}>
                {hoveredTask.title}
              </span>
              <br />
              <span style={{ fontWeight: "bold", fontSize: "12px" }}>
                {hoveredTask.status}
              </span>
            </Box>
          </InfoWindow>
        )}

        {activeTask && (
          <InfoWindow
            position={{ lat: activeTask.lat, lng: activeTask.lng }}
            onCloseClick={() => setActiveTask(null)}
          >
            <div style={{ minWidth: 200 }}>
              <h3
                style={{
                  margin: "0 0 8px 0",
                  color: taskStatusPinColor(activeTask.status),
                  cursor: "pointer",
                  textDecoration: "underline"
                }}
                onClick={() => onSelectTask && onSelectTask(activeTask.id)}
              >
                {activeTask.title}
              </h3>
              <p
                style={{
                  margin: "4px 0",
                  fontSize: "14px",
                  color: "#333",
                  fontWeight: "bold",
                  cursor: "pointer",
                  textDecoration: "underline"
                }}
                onClick={() => onSelectTask && onSelectTask(activeTask.id)}
              >
                👤 {activeTask.assignedWorkers}
              </p>
              <p style={{ margin: "4px 0", fontSize: "12px", color: "#666" }}>
                <strong>Status:</strong> {activeTask.status.replace('_', ' ').toUpperCase()}
              </p>
              <p style={{ margin: "4px 0", fontSize: "12px", color: "#666" }}>
                <strong>Priority:</strong> {activeTask.priority}
              </p>
              <p style={{ margin: "4px 0", fontSize: "12px", color: "#666" }}>
                <strong>Workers:</strong> {activeTask.completedWorkersCount}/{activeTask.assignedWorkersCount} completed
              </p>
              <p style={{ margin: "4px 0", fontSize: "12px", color: "#666" }}>
                <strong>Start:</strong> {formatTaskDateTime(activeTask.startAt)}
              </p>
              <p style={{ margin: "4px 0", fontSize: "12px", color: "#666" }}>
                <strong>End:</strong> {formatTaskDateTime(activeTask.endAt)}
              </p>
              <p style={{ margin: "4px 0", fontSize: "11px", color: "#999", wordBreak: "break-word" }}>
                📍 {activeTask.locationAddress}
              </p>
            </div>
          </InfoWindow>
        )}
      </GoogleMap>



      {(mapTaskId || statusFilter || priorityFilter) && (
        <IconButton
          // onClick={clearMapTask}
          onClick={() => {
            if (mapTaskId) clearMapTask();
            if (statusFilter) clearStatusFilter();
            if (priorityFilter) clearPriorityFilter();
          }}
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            zIndex: 1200,
            backgroundColor: "white",
            border: "1px solid #ccc"
          }}
        >
          <CloseIcon sx={{ color: "red" }} />
        </IconButton>
      )}


      {/* Full Map Button */}
      <CustomButton
        onClick={() => setIsFullMapOpen(true)}
        size="small"
        sx={{
          position: "absolute",
          top: 40,
          right: 12,
          backgroundColor: "#006EC2",
          color: "white",
          fontSize: "12px",
          padding: "4px 10px",
          borderRadius: "6px",
          zIndex: 1000, // always above the map
        }}
        label={<> {t("dashboard.viewFullMap")}
          <Box component="img" src={maparrow} alt="view icon" sx={{ ml: 1, width: 15, height: 13, objectFit: "contain" }} />
        </>}
      />


      {isFullMapOpen && (
        <Box sx={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", zIndex: 2000, backgroundColor: "white" }}>
          <IconButton
            onClick={() => setIsFullMapOpen(false)}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              zIndex: 2100,
              backgroundColor: "white",
              "&:hover": { backgroundColor: "white" },
            }}
          >
            <CloseIcon sx={{ color: "red" }} />
          </IconButton>

          <Box
            sx={{
              position: "absolute",
              top: 56,
              left: 16,
              right: 80,
              display: "flex",
              gap: 1,
              alignItems: "center",
              flexWrap: "wrap",
              zIndex: 2050,
            }}
          >
            {TASK_MAP_LEGEND.map((opt) => (
              <TaskMapLegendItem
                key={opt.statusKey}
                color={taskStatusPinColor(opt.statusKey)}
                label={t(opt.labelKey)}
                count={taskStatusCounts[opt.statusKey]}
              />
            ))}
          </Box>

          {/* Full map */}
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={getMapCenter()}
            zoom={5}
            onLoad={(map) => {
              fullMapRef.current = map;
              fitMapToMarkers(map);
            }}
            onUnmount={() => {
              fullMapRef.current = null;
            }}
            options={{
              fullscreenControl: false,
              mapTypeControl: false,
              streetViewControl: false,
            }}
          >
            {visibleMarkers.map((task) => (
              <React.Fragment key={task.id}>
                <Circle
                  center={{ lat: task.lat, lng: task.lng }}
                  radius={task.location_radius_m || 200}
                  options={{
                    fillColor: taskStatusPinColor(task.status),
                    strokeColor: taskStatusPinColor(task.status),
                    strokeWeight: 2,
                    strokeOpacity: 0.3,
                    fillOpacity: 0.1
                  }}
                />
                <Marker
                  position={{ lat: task.lat, lng: task.lng }}
                  icon={createCustomIcon(task.assignedWorkers, task.status)}
                  onClick={() => {
                    setActiveTask(task);
                    onSelectTask && onSelectTask(task.id);
                  }}
                  onMouseOver={() => setHoveredTask(task)}
                  onMouseOut={() => {
                    setHoveredTask((current) => (current?.id === task.id ? null : current));
                  }}
                />
              </React.Fragment>
            ))}

            {hoveredTask && (
              <InfoWindow
                position={{ lat: hoveredTask.lat, lng: hoveredTask.lng }}
                onCloseClick={() => setHoveredTask(null)}
              >
                <Box>
                  <span style={{ fontWeight: "bold", fontSize: "12px" }}>
                    {hoveredTask.title}
                  </span>
                  <br />
                  <span style={{ fontWeight: "bold", fontSize: "12px" }}>
                    {hoveredTask.status}
                  </span>
                </Box>
              </InfoWindow>
            )}

            {activeTask && (
              <InfoWindow
                position={{ lat: activeTask.lat, lng: activeTask.lng }}
                onCloseClick={() => setActiveTask(null)}
              >
                <div style={{ minWidth: 200 }}>
                  <h3
                    style={{
                      margin: "0 0 8px 0",
                      color: taskStatusPinColor(activeTask.status),
                      cursor: "pointer",
                      textDecoration: "underline"
                    }}
                    onClick={() => onSelectTask && onSelectTask(activeTask.id)}
                  >
                    {activeTask.title}
                  </h3>
                  <p
                    style={{
                      margin: "4px 0",
                      fontSize: "14px",
                      color: "#333",
                      fontWeight: "bold",
                      cursor: "pointer",
                      textDecoration: "underline"
                    }}
                    onClick={() => onSelectTask && onSelectTask(activeTask.id)}
                  >
                    👤 {activeTask.assignedWorkers}
                  </p>
                  <p style={{ margin: "4px 0", fontSize: "12px", color: "#666" }}>
                    <strong>Status:</strong> {activeTask.status.replace('_', ' ').toUpperCase()}
                  </p>
                  <p style={{ margin: "4px 0", fontSize: "12px", color: "#666" }}>
                    <strong>Priority:</strong> {activeTask.priority}
                  </p>
                  <p style={{ margin: "4px 0", fontSize: "12px", color: "#666" }}>
                    <strong>Workers:</strong> {activeTask.completedWorkersCount}/{activeTask.assignedWorkersCount} completed
                  </p>
                  <p style={{ margin: "4px 0", fontSize: "12px", color: "#666" }}>
                    <strong>Start:</strong> {formatTaskDateTime(activeTask.startAt)}
                  </p>
                  <p style={{ margin: "4px 0", fontSize: "12px", color: "#666" }}>
                    <strong>End:</strong> {formatTaskDateTime(activeTask.endAt)}
                  </p>
                  <p style={{ margin: "4px 0", fontSize: "11px", color: "#999", wordBreak: "break-word" }}>
                    📍 {activeTask.locationAddress}
                  </p>
                </div>
              </InfoWindow>
            )}
          </GoogleMap>

        </Box>
      )}
      </Box>
    </Box>
  );
};

export default MapTask;
